import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { ZITI_DATA_SERVICE, ZitiDataService } from 'ziti-console-lib';
import { GrowlerData, GrowlerService } from '@netfoundry-ui/shared/growler';
import {
    buildInitialFormData,
    CreationStatus,
    formatFieldLabel,
    formatPortRangeChips,
    isPortRangeField,
    parsePortRangeChips,
    ServiceFromTemplateService,
    ServiceTemplate,
    ServiceTemplateConfigType,
    ServiceTemplateField,
    ServiceTemplateService,
    TemplateFormData,
    TemplateValidationError,
    validateTemplateForm,
} from '@netfoundry-ui/shared/service-templates';
import { Subscription } from 'rxjs';

const EMPTY_ARRAY: any[] = [];

interface EndpointFields {
    protocol?: ServiceTemplateField;
    address?: ServiceTemplateField;
    port?: ServiceTemplateField;
    protocolOptions: string[];
}

interface FieldLayoutGroup {
    title: string | null;
    forwarding: boolean;
    fields: ServiceTemplateField[];
    pairs: { control: ServiceTemplateField; dependents: ServiceTemplateField[] }[];
    endpoint?: EndpointFields;
    required?: boolean;
}

// A curated group spec is an ordered list; 'forwarding' is the auto-built
// dependentOf group, an `endpoint` group renders protocol://address:port as one
// composer, the rest are titled field buckets. Field paths not listed here still
// render (in a trailing "More" group), so a schema change can't drop a field.
type FieldGroupSpec =
    | 'forwarding'
    | { title: string; fields: string[] }
    | { title: string; endpoint: { protocol: string; address: string; port: string }; required?: boolean };

// Keyed by Ziti config-type name (dotted, e.g. host.v1 — not camelCase).
/* eslint-disable @typescript-eslint/naming-convention */
const CURATED_GROUPS: Record<string, FieldGroupSpec[]> = {
    'host.v1': [
        { title: 'Target', endpoint: { protocol: 'protocol', address: 'address', port: 'port' }, required: true },
        'forwarding',
        // maxConnections/connectTimeout live under Ziti's `listenOptions` — use
        // that term (and it matches the template-builder page's group name).
        { title: 'Listen Options', fields: ['listenOptions.maxConnections', 'listenOptions.connectTimeout'] },
    ],
};
/* eslint-enable @typescript-eslint/naming-convention */

@Component({
    selector: 'netfoundry-ui-create-service-from-template',
    templateUrl: './create-service-from-template.component.html',
    styleUrls: ['./create-service-from-template.component.scss'],
    standalone: false,
})
export class CreateServiceFromTemplateComponent implements OnInit, OnDestroy {
    templates: ServiceTemplate[] = [];
    selectedTemplateId = '';
    readonly templateShowMoreStep = 4;
    templateVisibleCount = this.initialTemplateCount();
    templateSearch = '';
    selectedTemplate: ServiceTemplate | null = null;

    serviceName = '';
    serviceRoleAttributes: string[] = [];
    encryptionRequired = true;
    terminatorStrategy = 'smartrouting';

    formData: TemplateFormData = {};
    tempArrayObjects: Record<string, Record<string, any>> = {};
    // Config sub-panels collapsed by the user (empty = all expanded). Keyed by configTypeId.
    collapsedConfigIds = new Set<string>();

    dialPolicyAttributes: string[] = [];
    bindPolicyAttributes: string[] = [];
    availableRoleAttributes: string[] = [];

    validationErrors: TemplateValidationError[] = [];
    nameError = '';

    creationStatuses: CreationStatus[] = [];
    isCreating = false;
    creationFinished = false;
    hasErrors = false;

    private subscription = new Subscription();
    private queryTemplateIdApplied = false;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private templateService: ServiceTemplateService,
        private serviceFromTemplateService: ServiceFromTemplateService,
        private growlerService: GrowlerService,
        @Inject(ZITI_DATA_SERVICE) private zitiService: ZitiDataService
    ) {}

    ngOnInit(): void {
        this.subscription.add(
            this.templateService.templates$.subscribe((templates) => {
                this.templates = templates;
                this.applyQueryTemplateId(templates);
            })
        );
        this.templateService.loadTemplates();

        this.zitiService
            .get('identity-role-attributes', {}, [])
            .then((result: any) => (this.availableRoleAttributes = result?.data || []))
            .catch(() => (this.availableRoleAttributes = []));
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    get filteredTemplates(): ServiceTemplate[] {
        const term = this.templateSearch.trim().toLowerCase();
        if (!term) {
            return this.templates;
        }
        return this.templates.filter((t) => `${t.name} ${t.description ?? ''}`.toLowerCase().includes(term));
    }

    get visibleTemplates(): ServiceTemplate[] {
        const filtered = this.filteredTemplates;
        const selectedIndex = filtered.findIndex((t) => t.id === this.selectedTemplateId);
        return filtered.slice(0, Math.max(this.templateVisibleCount, selectedIndex + 1));
    }

    get hiddenTemplateCount(): number {
        return this.filteredTemplates.length - this.visibleTemplates.length;
    }

    showMoreTemplates(): void {
        this.templateVisibleCount = this.visibleTemplates.length + this.templateShowMoreStep;
    }

    onTemplateSearch(value: string): void {
        this.templateSearch = value;
        this.templateVisibleCount = this.initialTemplateCount();
    }

    // Two rows of cards: the grid is 2 columns on narrow screens, 3 on wide ones.
    private initialTemplateCount(): number {
        const width = typeof window === 'undefined' ? 1280 : window.innerWidth;
        return width < 1100 ? 4 : 6;
    }

    selectTemplate(id: string): void {
        this.selectedTemplateId = id;
        this.onTemplateChange();
    }

    onTemplateChange(): void {
        this.selectedTemplate = this.templates.find((t) => t.id === this.selectedTemplateId) || null;
        this.formData = this.selectedTemplate ? buildInitialFormData(this.selectedTemplate.configTypes) : {};
        this.fieldGroupsCache.clear();
        this.collapsedConfigIds.clear();
        this.tempArrayObjects = {};
        this.validationErrors = [];
        this.nameError = '';
        this.serviceRoleAttributes = [];
        this.dialPolicyAttributes = [];
        this.bindPolicyAttributes = [];
    }

    /** True when the selected template has at least one config type with visible fields —
     *  gates the whole "Service Configuration" panel. */
    get hasVisibleConfigTypes(): boolean {
        return !!this.selectedTemplate?.configTypes.some((ct) => this.getVisibleFields(ct).length > 0);
    }

    toggleConfigCollapsed(configTypeId: string): void {
        if (this.collapsedConfigIds.has(configTypeId)) {
            this.collapsedConfigIds.delete(configTypeId);
        } else {
            this.collapsedConfigIds.add(configTypeId);
        }
    }

    isConfigCollapsed(configTypeId: string): boolean {
        return this.collapsedConfigIds.has(configTypeId);
    }

    onNameChange(value: string): void {
        this.serviceName = value;
        if (this.nameError) {
            this.nameError = '';
        }
    }

    /** Stable, selector-safe DOM id for a field's container — used to associate
     *  labels/controls and to scroll to the first validation error. */
    fieldDomId(configTypeId: string, fieldPath: string): string {
        return `stpl-f-${configTypeId}-${fieldPath}`.replace(/[^a-zA-Z0-9_-]/g, '-');
    }

    /** A required field the user hasn't filled — surfaced in the preview so the
     *  missing bits read as "Required" rather than a neutral "Not set". */
    isPreviewRequiredMissing(configTypeId: string, field: ServiceTemplateField): boolean {
        return !!field.required && this.isPreviewValueEmpty(configTypeId, field);
    }

    // Pre-selects the template chosen on the /services/select card, passed via
    // ?templateId=. Applied at most once so it never fights a later manual
    // re-selection if templates$ re-emits; a missing/stale id is a silent
    // no-op, falling back to the normal unselected <select> state.
    private applyQueryTemplateId(templates: ServiceTemplate[]): void {
        if (this.queryTemplateIdApplied) {
            return;
        }
        const queryTemplateId = this.route.snapshot.queryParamMap.get('templateId');
        if (queryTemplateId && templates.some((t) => t.id === queryTemplateId)) {
            this.queryTemplateIdApplied = true;
            this.selectedTemplateId = queryTemplateId;
            this.onTemplateChange();
        }
    }

    getVisibleFields(ct: ServiceTemplateConfigType): ServiceTemplateField[] {
        return ct.fields.filter((f) => f.includeInForm !== false);
    }

    private fieldGroupsCache = new Map<string, FieldLayoutGroup[]>();

    /** Lays a config type's visible fields into purpose groups instead of one flat
     *  list. Derived from the schema (no per-type hardcoding): fields that gate
     *  others (`dependentOf`, e.g. the forward* toggles) and the fields they gate
     *  form a "Forwarding" group of paired rows; everything else is a plain grid.
     *  Memoized per config type — the schema is static once loaded. */
    getFieldGroups(ct: ServiceTemplateConfigType): FieldLayoutGroup[] {
        const cached = this.fieldGroupsCache.get(ct.configTypeId);
        if (cached) {
            return cached;
        }
        const visible = this.getVisibleFields(ct);
        const byPath = new Map(visible.map((f) => [f.fieldPath, f]));
        const controllers = visible.filter((f) => f.dependentOf && f.dependentOf.length > 0);
        const dependentPaths = new Set<string>();
        controllers.forEach((c) => (c.dependentOf || []).forEach((p) => dependentPaths.add(p)));

        const forwardingGroup = (): FieldLayoutGroup | null => {
            if (!controllers.length) {
                return null;
            }
            const pairs = controllers.map((control) => ({
                control,
                dependents: (control.dependentOf || [])
                    .map((p) => byPath.get(p))
                    .filter((f): f is ServiceTemplateField => !!f),
            }));
            return { title: 'Forwarding', forwarding: true, fields: [], pairs };
        };

        const groups: FieldLayoutGroup[] = [];
        const spec = CURATED_GROUPS[ct.configTypeName];
        if (spec) {
            // Known config type: lay fields into curated, titled groups in order,
            // with Forwarding auto-built from dependentOf wherever the spec marks it.
            const placed = new Set<string>();
            spec.forEach((s) => {
                if (s === 'forwarding') {
                    const fwd = forwardingGroup();
                    if (fwd) {
                        groups.push(fwd);
                        controllers.forEach((c) => placed.add(c.fieldPath));
                        dependentPaths.forEach((p) => placed.add(p));
                    }
                    return;
                }
                if ('endpoint' in s) {
                    const protocol = byPath.get(s.endpoint.protocol);
                    const address = byPath.get(s.endpoint.address);
                    const port = byPath.get(s.endpoint.port);
                    const present = [protocol, address, port].filter((f): f is ServiceTemplateField => !!f);
                    present.forEach((f) => placed.add(f.fieldPath));
                    if (present.length >= 2) {
                        groups.push({
                            title: s.title,
                            forwarding: false,
                            fields: [],
                            pairs: [],
                            endpoint: {
                                protocol,
                                address,
                                port,
                                protocolOptions: this.getProtocolOptions(ct, protocol),
                            },
                            required: s.required,
                        });
                    } else if (present.length === 1) {
                        // Only one part included — a composer would be odd; render it plainly.
                        groups.push({ title: s.title, forwarding: false, fields: present, pairs: [] });
                    }
                    return;
                }
                const fields = s.fields.map((p) => byPath.get(p)).filter((f): f is ServiceTemplateField => !!f);
                fields.forEach((f) => placed.add(f.fieldPath));
                if (fields.length) {
                    groups.push({ title: s.title, forwarding: false, fields, pairs: [] });
                }
            });
            // Anything the spec didn't account for still shows, so nothing is lost.
            const leftover = visible.filter((f) => !placed.has(f.fieldPath));
            if (leftover.length) {
                groups.push({ title: 'More', forwarding: false, fields: leftover, pairs: [] });
            }
        } else {
            // Unknown config type: a plain grid of the independent fields, plus the
            // dependency-derived Forwarding group if any.
            const primary = visible.filter(
                (f) => !(f.dependentOf && f.dependentOf.length) && !dependentPaths.has(f.fieldPath)
            );
            if (primary.length) {
                groups.push({
                    title: controllers.length ? 'Settings' : null,
                    forwarding: false,
                    fields: primary,
                    pairs: [],
                });
            }
            const fwd = forwardingGroup();
            if (fwd) {
                groups.push(fwd);
            }
        }

        this.fieldGroupsCache.set(ct.configTypeId, groups);
        return groups;
    }

    isFieldOn(configTypeId: string, fieldPath: string): boolean {
        return this.getFieldValue(configTypeId, fieldPath) === true;
    }

    /** Options for the endpoint protocol select: the field's own enum if it has
     *  one, else the sibling allowedProtocols enum (same valid set), else tcp/udp. */
    private getProtocolOptions(ct: ServiceTemplateConfigType, protocol?: ServiceTemplateField): string[] {
        if (protocol?.enumOptions?.length) {
            return protocol.enumOptions;
        }
        const allowed = ct.fields.find((f) => f.fieldPath === 'allowedProtocols');
        if (allowed?.enumOptions?.length) {
            return allowed.enumOptions;
        }
        return ['tcp', 'udp'];
    }

    fieldKey(ct: ServiceTemplateConfigType, field: ServiceTemplateField): string {
        return `${ct.configTypeId}:${field.fieldPath}`;
    }

    getFieldValue(configTypeId: string, fieldPath: string): any {
        return this.formData[configTypeId]?.[fieldPath];
    }

    setFieldValue(configTypeId: string, fieldPath: string, value: any): void {
        if (!this.formData[configTypeId]) {
            this.formData[configTypeId] = {};
        }
        this.formData[configTypeId][fieldPath] = value;
        this.validationErrors = this.validationErrors.filter(
            (e) => !(e.configType === configTypeId && e.field === fieldPath)
        );
    }

    hasFieldError(configTypeId: string, fieldPath: string): boolean {
        return this.validationErrors.some((e) => e.configType === configTypeId && e.field === fieldPath);
    }

    getFieldError(configTypeId: string, fieldPath: string): string {
        return this.validationErrors.find((e) => e.configType === configTypeId && e.field === fieldPath)?.message || '';
    }

    getArrayItems(configTypeId: string, fieldPath: string): any[] {
        return this.formData[configTypeId]?.[fieldPath] || EMPTY_ARRAY;
    }

    isPortRangeField(field: ServiceTemplateField): boolean {
        return isPortRangeField(field);
    }

    private portRangeChipsCache = new Map<string, { source: any[]; chips: string[] }>();

    getPortRangeChips(configTypeId: string, fieldPath: string): string[] {
        const key = this.fieldKey({ configTypeId } as ServiceTemplateConfigType, { fieldPath } as ServiceTemplateField);
        const source = this.getArrayItems(configTypeId, fieldPath);
        const cached = this.portRangeChipsCache.get(key);
        if (cached && cached.source === source) {
            return cached.chips;
        }
        const chips = formatPortRangeChips(source);
        this.portRangeChipsCache.set(key, { source, chips });
        return chips;
    }

    setPortRangeChips(configTypeId: string, fieldPath: string, chips: string[]): void {
        this.setFieldValue(configTypeId, fieldPath, parsePortRangeChips(chips));
    }

    getTempObjectValue(key: string, propName: string): any {
        return this.tempArrayObjects[key]?.[propName] || '';
    }

    setTempObjectValue(key: string, propName: string, value: any): void {
        if (!this.tempArrayObjects[key]) {
            this.tempArrayObjects[key] = {};
        }
        this.tempArrayObjects[key][propName] = value;
    }

    addArrayItem(configTypeId: string, field: ServiceTemplateField): void {
        const key = this.fieldKey({ configTypeId } as ServiceTemplateConfigType, field);
        const array = (this.formData[configTypeId][field.fieldPath] as any[]) || [];

        const tempObj = this.tempArrayObjects[key] || {};
        if (Object.keys(tempObj).some((k) => tempObj[k] !== undefined && tempObj[k] !== '')) {
            array.push({ ...tempObj });
            this.tempArrayObjects[key] = {};
        }
        this.formData[configTypeId][field.fieldPath] = array;
    }

    removeArrayItem(configTypeId: string, fieldPath: string, index: number): void {
        const array = this.formData[configTypeId][fieldPath] as any[];
        array.splice(index, 1);
    }

    isEnumOptionSelected(configTypeId: string, fieldPath: string, option: string): boolean {
        const value = this.getFieldValue(configTypeId, fieldPath);
        return Array.isArray(value) && value.includes(option);
    }

    toggleEnumOption(configTypeId: string, fieldPath: string, option: string, event: Event): void {
        const checked = (event.target as HTMLInputElement).checked;
        let currentValue = this.getFieldValue(configTypeId, fieldPath);
        if (!Array.isArray(currentValue)) {
            currentValue = [];
        }
        currentValue = checked ? [...currentValue, option] : currentValue.filter((v: string) => v !== option);
        this.setFieldValue(configTypeId, fieldPath, currentValue);
    }

    formatFieldLabel(key: string): string {
        return formatFieldLabel(key);
    }

    formatArrayItem(item: any): string {
        if (typeof item === 'object' && item !== null) {
            return Object.entries(item)
                .map(([k, v]) => `${formatFieldLabel(k)}: ${v}`)
                .join(', ');
        }
        return String(item);
    }

    isPreviewValueEmpty(configTypeId: string, field: ServiceTemplateField): boolean {
        if (field.type === 'array') {
            return this.getArrayItems(configTypeId, field.fieldPath).length === 0;
        }
        const value = this.getFieldValue(configTypeId, field.fieldPath);
        return value === undefined || value === null || value === '';
    }

    isPreviewChipField(field: ServiceTemplateField): boolean {
        return field.type === 'array';
    }

    getPreviewChips(configTypeId: string, field: ServiceTemplateField): string[] {
        if (this.isPortRangeField(field)) {
            return this.getPortRangeChips(configTypeId, field.fieldPath);
        }
        const items = this.getArrayItems(configTypeId, field.fieldPath);
        return field.arrayItemType === 'object' ? items.map((item) => this.formatArrayItem(item)) : items.map(String);
    }

    formatPreviewValue(configTypeId: string, field: ServiceTemplateField): string {
        const value = this.getFieldValue(configTypeId, field.fieldPath);
        return field.type === 'boolean' ? (value === true ? 'Yes' : 'No') : String(value);
    }

    private validate(): boolean {
        this.nameError = '';
        this.validationErrors = [];

        if (!this.serviceName.trim()) {
            this.nameError = 'Service name is required';
        }
        if (!this.selectedTemplate) {
            return false;
        }
        this.validationErrors = validateTemplateForm(this.selectedTemplate.configTypes, this.formData);
        this.validateEndpointTargets();
        return !this.nameError && this.validationErrors.length === 0;
    }

    /** A config type with a required endpoint (Target) needs a usable target —
     *  a fixed address, or forwarding turned on — mirroring Ziti's fixed-vs-forward
     *  semantics (address/port/protocol are schema-optional on their own). */
    private validateEndpointTargets(): void {
        (this.selectedTemplate?.configTypes || []).forEach((ct) => {
            const groups = this.getFieldGroups(ct);
            const endpointGroup = groups.find((g) => g.endpoint && g.required);
            const address = endpointGroup?.endpoint?.address;
            if (!endpointGroup || !address) {
                return;
            }
            const addressValue = this.getFieldValue(ct.configTypeId, address.fieldPath);
            const addressSet = addressValue != null && String(addressValue).trim() !== '';
            const forwardingGroup = groups.find((g) => g.forwarding);
            const forwardingOn = (forwardingGroup?.pairs || []).some((p) =>
                this.isFieldOn(ct.configTypeId, p.control.fieldPath)
            );
            if (!addressSet && !forwardingOn) {
                this.validationErrors.push({
                    configType: ct.configTypeId,
                    field: address.fieldPath,
                    message: 'Set an address or enable forwarding to define a target',
                });
            }
        });
    }

    create(): void {
        if (!this.validate() || !this.selectedTemplate) {
            this.scrollToFirstError();
            return;
        }

        this.isCreating = true;
        this.creationFinished = false;
        this.hasErrors = false;

        const { status$, result } = this.serviceFromTemplateService.createServiceFromTemplate({
            template: this.selectedTemplate,
            serviceName: this.serviceName,
            serviceRoleAttributes: this.serviceRoleAttributes,
            encryptionRequired: this.encryptionRequired,
            terminatorStrategy: this.terminatorStrategy,
            formData: this.formData,
            dialPolicyAttributes: this.dialPolicyAttributes,
            bindPolicyAttributes: this.bindPolicyAttributes,
        });

        this.subscription.add(status$.subscribe((statuses) => (this.creationStatuses = statuses)));

        result.then(({ hasErrors }) => {
            this.isCreating = false;
            this.creationFinished = true;
            this.hasErrors = hasErrors;

            if (!hasErrors) {
                this.growlerService.show(
                    new GrowlerData(
                        'success',
                        'Success',
                        'Service Created',
                        `"${this.serviceName}" was created successfully.`
                    )
                );
            }
        });
    }

    /** After a blocked submit, bring the first error into view (and focus the
     *  name field when that's the offender) — Save lives in the sticky header, so
     *  errors further down the form would otherwise go unseen. */
    private scrollToFirstError(): void {
        setTimeout(() => {
            let el: HTMLElement | null = null;
            if (this.nameError) {
                el = document.getElementById('ServiceTemplateServiceName');
            } else if (this.validationErrors.length) {
                const first = this.validationErrors[0];
                el = document.getElementById(this.fieldDomId(first.configType, first.field));
            }
            if (!el) {
                return;
            }
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            if (typeof (el as HTMLElement).focus === 'function' && el.tagName === 'INPUT') {
                (el as HTMLElement).focus({ preventScroll: true });
            }
        });
    }

    formatErrorDetails(error: any): string {
        return this.serviceFromTemplateService.formatErrorDetails(error);
    }

    toggleErrorDetails(status: CreationStatus): void {
        status.showDetails = !status.showDetails;
    }

    goToServices(): void {
        this.router.navigateByUrl('/services');
    }

    /** Worklist mode: this form is creating one destination from a multi-destination segment. */
    get isWorklist(): boolean {
        return !!this.route.snapshot.queryParamMap.get('active');
    }

    get worklistTotal(): number {
        return this.splitList(this.route.snapshot.queryParamMap.get('destPairs')).length;
    }

    /** Destinations still to create once the current one succeeds. */
    get worklistRemaining(): number {
        const done = new Set(this.splitList(this.route.snapshot.queryParamMap.get('done')));
        const active = this.route.snapshot.queryParamMap.get('active');
        if (active && !this.hasErrors) {
            done.add(active);
        }
        return Math.max(0, this.worklistTotal - done.size);
    }

    /** Return to the destination worklist, marking the active one done when the create succeeded. */
    backToWorklist(): void {
        const p = this.route.snapshot.queryParamMap;
        const params: Params = { method: 'template' };
        ['destPairs', 'srcIdentity', 'srcIp', 'start', 'end', 'label'].forEach((key) => {
            const value = p.get(key);
            if (value) {
                params[key] = value;
            }
        });
        const done = this.splitList(p.get('done'));
        const active = p.get('active');
        if (active && !this.hasErrors && !done.includes(active)) {
            done.push(active);
        }
        if (done.length) {
            params['done'] = done.join(',');
        }
        this.router.navigate(['/services/select'], { queryParams: params });
    }

    private splitList(value: string | null): string[] {
        return (value || '')
            .split(',')
            .map((v) => v.trim())
            .filter(Boolean);
    }

    back(): void {
        // Step back to the chooser, carrying the traffic selection and marking From template as chosen
        // so it re-renders where the user left off.
        this.router.navigate(['/services/select'], { queryParams: this.backParams() });
    }

    private backParams(): Params {
        const p = this.route.snapshot.queryParamMap;
        const params: Params = { method: 'template' };
        ['destIp', 'destPort', 'destPairs', 'done', 'srcIdentity', 'srcIp', 'start', 'end', 'label'].forEach((key) => {
            const value = p.get(key);
            if (value) {
                params[key] = value;
            }
        });
        return params;
    }

    goToTemplates(): void {
        this.router.navigateByUrl('/service-templates');
    }

    onHeaderAction(event: { name: string }): void {
        if (event.name === 'save') {
            this.create();
        } else if (event.name === 'close') {
            this.back();
        }
    }
}
